import debug from 'Helper/debugHelpers'
import globalConfig from 'Helper/configHelpers'
import { EventSourcePolyfill } from 'event-source-polyfill'
import {
  jsonResponseHandler,
  blobResponseHandler,
  errorResponseHandler
} from 'Helper/API/ResponseHandlers.js'

const calculateParams = (resource, config, transaction) => {
  const { sort, limit, offset, shape, downlink } = config
  let params
  const paramsArray = []
  if (limit != null) {
    paramsArray.push('limit=' + limit)
  }
  if (offset != null) {
    paramsArray.push('offset=' + offset)
  }
  if (sort != null) {
    paramsArray.push('sort=' + encodeURI(sort))
  }
  if (shape != null) {
    paramsArray.push('shape=' + encodeURI(shape))
  }
  if (downlink != null) {
    const speed = navigator?.connection?.downlink
    if (speed) {
      paramsArray.push('link=' + speed)
    }
  }
  const queryParams = paramsArray.length > 0 ? '?' + paramsArray.join('&') : ''

  const split = resource.split('?')
  const resourceUri = split[0]

  if (split[1] != null && queryParams === '') {
    params = '?' + split[1]
  } else if (split[1] != null) {
    params = queryParams + '&' + split[1]
  } else {
    params = queryParams
  }

  if (params.includes('?') && transaction.startsWith('?')) {
    params += transaction.replace('?', '&')
  } else {
    params += transaction
  }

  return { params, resourceUri }
}
export const get = (
  token,
  resource,
  method,
  id = '',
  obj = null,
  transaction = '',
  config = {}
) => {
  let body = null
  let idAddition = ''
  let tail = ''

  // API routes start with v1 unless explicitly specified otherwise.
  const versionRegex = /^(v[0-9]\/)/
  if (!versionRegex.test(resource)) {
      resource = `v1/${resource}`
  }

  const { params, resourceUri } = calculateParams(resource, config, transaction)

  if (id !== '' && id !== null) {
    idAddition = '/' + id
  }

  switch (method.toUpperCase()) {
    case 'STREAM':
      if (resource.includes('?')) {
        const parts = resource.split('?')
        resource = parts[0] + '/_stream' + '?' + parts[1]
      } else {
        resource = resource + '/_stream'
      }
      return stream(globalConfig.api_base_url + '/' + resource, token)
    case 'CREATE':
      method = 'POST'
      break
    case 'READ_ALL':
    case 'READ':
      method = 'GET'
      break
    case 'SEARCH':
      method = 'POST'
      tail = '/_search'
      break
    case 'UPDATE':
      method = 'PUT'
      break
    case 'DELETE':
      method = 'DELETE'
      break
    case 'PATCH':
      method = 'PATCH'
      break
    default:
      return {
        error: 'bad API method',
        status: 'Xtract API does not support method:' + method
      }
  }

  if (obj !== null && method !== 'GET') {
    body = JSON.stringify(obj)
  }

  const headers = {
    'Content-Type': 'application/json,text/html',
    Accept: 'application/json',
    Authorization: 'Bearer ' + token,
    xtractAppId: globalConfig.client_id
  }

  // Check if this is a route that requires CSRF token
  // Remove query params and version prefix to check the actual route
  const routeWithoutParams = resource.split('?')[0]
  const routeWithoutVersion = routeWithoutParams.replace(/^v[0-9]+\//, '')
  const routeParts = routeWithoutVersion.split('/')
  
  if (
    routeParts[0] === 'me' ||
    routeParts[0] === 'system_status' ||
    routeParts[0] === 'oauth'
  ) {
    // me is a special api route that requires csrf validation
    // system status is used as the session keep alive heart beat so that if the user leave summit on the login screen for a long time
    // and then tries to login they dont get a csrf error.
    headers['X-CSRF-TOKEN'] = globalConfig.getCsrfToken()
  }

  const request = new Request(
    `${globalConfig.api_base_url}/${resourceUri}${idAddition}${tail}${params}`,
    {
      method,
      credentials: 'same-origin', // really the only route that uses this is /me so that the session cookie is sent
      body,
      cache: 'no-cache',
      mode: 'cors',
      headers: new Headers(headers),
      signal: config?.sequentialControllerRef?.current?.signal
    }
  )
  return fetch(request)
    .then(data => jsonResponseHandler(data))
    .catch(error => errorResponseHandler(error))
}

export const download = (
  token,
  resource,
  transaction = '',
  contentType = 'application/pdf'
) => {
  const body = null

  // API routes start with v1 unless explicitly specified otherwise.
  const versionRegex = /^(v[0-9]\/)/
  if (!versionRegex.test(resource)) {
      resource = `v1/${resource}`
  }

  const request = new Request(
    `${globalConfig.api_base_url}/${resource}${transaction}`,
    {
      method: 'GET',
      body,
      credentials: 'omit',
      cache: 'no-cache',
      mode: 'cors',
      headers: new Headers({
        Accept: contentType,
        Authorization: 'Bearer ' + token,
        xtractAppId: globalConfig.client_id
      })
    }
  )
  return fetch(request)
    .then(blobResponseHandler)
    .catch(error => errorResponseHandler(error))
}

export const stream = (url, token) => {
  if (window.EventSource) {
    try {
      return new EventSourcePolyfill(url, {
        headers: {
          Authorization: 'Bearer ' + token,
          Accept: 'application/json,text/html',
          xtractAppId: globalConfig.client_id
        }
      })
    } catch (e) {
      debug.logObject(
        'xtract_api stream error:',
        e,
        debug.logLevels.API,
        'error'
      )
    }
  } else {
    return -1
  }
}
